// client/src/features/expenses/recurring-search.ts — the URL state of SCR-EXP-003 · EXP-005
// phase 2 (04 §9.2.3).
//
// The list's filter names are `GET /api/v1/recurring-expenses`' own — `propertyId`,
// `categoryId`, `active`, `q` — so the URL and the request say the same words. `active` is a
// STRING in the URL for the reason `search.ts` gives for `isCapex` (D-713): `z.coerce.boolean()`
// turns „false" into `true`, and a transforming schema is one the router's `navigate` refuses.
// `toRecurringQuery` in `queries.ts` does the one conversion.
//
// No `sort`: the endpoint's default order is the store's, and nothing on the screen re-sorts.
// The rule that is OPEN — in the history panel, in the builder — is component state and not a
// URL parameter: a modal is not a place, and Back should leave the page rather than close it.

import { z } from 'zod';

import { urlBoolean, zUrlBoolean } from '@/lib/url-state';

export const recurringSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  q: z.string().trim().optional(),
  propertyId: z.string().trim().optional(),
  categoryId: z.string().trim().optional(),
  /** Both spellings, for the reason `lib/url-state.ts` documents (D-1197). */
  active: zUrlBoolean.optional(),
});

export type RecurringSearch = z.infer<typeof recurringSearchSchema>;

/** The URL's tri-state as the endpoint's `active` query parameter takes it. */
export function activeFilter(search: RecurringSearch): boolean | undefined {
  return urlBoolean(search.active);
}

export function hasActiveRecurringFilters(search: RecurringSearch): boolean {
  return (
    (search.q !== undefined && search.q !== '') ||
    search.propertyId !== undefined ||
    search.categoryId !== undefined ||
    search.active !== undefined
  );
}

export const CLEARED_RECURRING_SEARCH: RecurringSearch = { page: 1, pageSize: 25 };
